/**
 * Katalog tema tampilan.
 *
 * Satu-satunya daftar tema di aplikasi ini. Nama classnya harus sama persis
 * dengan yang ada di `styles/globals.css` DAN dengan skrip kecil di
 * `index.html` yang memasang tema sebelum React ter-mount — tiga tempat yang
 * mudah berselisih, sehingga dua di antaranya membaca daftar ini dan yang
 * ketiga (index.html, yang tidak bisa mengimpor apa pun) diberi catatan
 * silang di sana.
 *
 * Tema tidak menambah aturan tata letak apa pun; seluruhnya hanya menimpa
 * variabel warna. Itu sebabnya menambah tema di sini nyaris tanpa biaya.
 */

export type ThemeId = 'light' | 'dark' | 'kaca-malam' | 'neon-arus' | 'aurora';

export interface ThemeOption {
  id: ThemeId;
  label: string;
  /** Keterangan singkat di pemilih tema — membantu memilih tanpa mencoba satu per satu. */
  description: string;
  /**
   * Class pada <html>. Kosong untuk tema terang: itu nilai bawaan di
   * `:root`, jadi tidak ada yang perlu dipasang.
   */
  className: string;
  /** Untuk contoh warna kecil di pemilihnya — latar, kartu, dan aksen. */
  swatch: [string, string, string];
}

export const THEMES: ThemeOption[] = [
  {
    id: 'light',
    label: 'Terang',
    description: 'Tampilan bawaan',
    className: '',
    swatch: ['oklch(99% 0.003 240)', 'oklch(96% 0.008 250)', 'oklch(51% 0.105 185)'],
  },
  {
    id: 'dark',
    label: 'Gelap',
    description: 'Nyaman dipakai malam hari',
    className: 'dark',
    swatch: ['oklch(17% 0.015 250)', 'oklch(26% 0.017 250)', 'oklch(70% 0.12 185)'],
  },
  {
    id: 'kaca-malam',
    label: 'Kaca Malam',
    description: 'Indigo tenang, sian ke ungu',
    className: 'theme-kaca-malam',
    swatch: ['#0b0e1a', '#121728', '#38bdf8'],
  },
  {
    id: 'neon-arus',
    label: 'Neon Arus',
    description: 'Magenta elektrik, paling berani',
    className: 'theme-neon-arus',
    swatch: ['#07060e', '#100c1c', '#f472b6'],
  },
  {
    id: 'aurora',
    label: 'Aurora',
    description: 'Toska ke biru, seperti cahaya kutub',
    className: 'theme-aurora',
    swatch: ['#04120f', '#0a1d1b', '#2dd4bf'],
  },
];

/** Seluruh class tema — dilepas semuanya sebelum yang terpilih dipasang. */
export const THEME_CLASSES = THEMES.map((theme) => theme.className).filter(Boolean);

export function isThemeId(value: unknown): value is ThemeId {
  return THEMES.some((theme) => theme.id === value);
}

/**
 * Tema ini bertumpu pada latar gelap.
 *
 * Dipakai layar yang hanya butuh tahu "sedang gelap atau tidak" — mis. Portal
 * Wali Murid, yang tombolnya cukup terang/gelap dan tidak perlu tahu ada tema
 * neon sama sekali.
 */
export function isDarkTheme(id: ThemeId): boolean {
  return id !== 'light';
}

export function themeById(id: ThemeId): ThemeOption {
  // Daftar di atas memuat seluruh anggota ThemeId, jadi pencarian ini
  // tidak mungkin meleset — `!` di sini menyatakan itu, bukan menutupinya.
  return THEMES.find((theme) => theme.id === id)!;
}
